import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Container,
  FormControl,
  InputGroup,
  Button,
  Spinner,
  Card,
  Table,
  Form,
  Badge,
} from 'react-bootstrap';
import { useOnboardingOptional } from '../onboarding/OnboardingContext';
import OnboardingEmptyHero from '../onboarding/OnboardingEmptyHero';
import Loading from '../shared/loading/Loading';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useLoading } from '../../hooks/useLoading';
import {
  loadConfigurationFactors,
  isProfitPlus2k8ReadyForAppFeatures,
  isProfitPlus2k8StockManagedInMayorX,
  requestOrderSyncToProfit,
  requestProductInventoryRefreshFromProfit,
} from '../../api/configuration';
import { getDoc, doc } from 'firebase/firestore';
import { firestore } from '../../utils/firebaseSetUp';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import {
  formatDateEsFromFirestore,
  formatTimeEsFromFirestore,
} from '../../utils/format';
import { sortOrdersByCreatedThenSaleNumber } from '../../utils/orderListSort';
import { isSelfHosted } from '../../config/infra';
import {
  Order,
  loadOrders,
  subscribeToOrders,
  fetchOrderById,
  collectFullProfitOrderSyncIssues,
} from '../../api/orders';
import {
  getOrdersServer,
  getOrderServer,
  rejectOrderServer,
  deleteOrderServer,
  sendDeliveryNoteServer,
  markOrderAsInvoicedServer,
} from '../../api/ordersServer';
import {
  Search as FiSearch,
  ArrowLeft as FiArrowLeft,
  Trash3 as FiTrash2,
  Plus as FiPlus,
  Palette as FiPalette,
  QuestionCircle as FiQuestionCircle,
} from 'react-bootstrap-icons';
import {
  resolveOrderCreatorDisplay,
  pascalCaseWords,
  type OrderCreatorDisplayInfo,
} from './orderCreatorDisplay';
import styles from './Orders.module.scss';
import listStyles from '../shared/layout/dataListTable.module.scss';
import OrderForm from './OrderForm';
import OrderRowActions from './OrderRowActions';
import OrderView, {
  DeliveryNotePrintOptions,
  DeliveryNoteSendOptions,
} from './OrderView';
import TrashBin from './TrashBin';
import { PANEL_PERMISSIONS } from '../../utils/panelPermissions';
import {
  PanelReadOnlyBanner,
  RequirePanelWrite,
} from '../auth/PanelWriteGate';
import { useCanWritePanelArea } from '../../contexts/PanelAccessContext';
import DeliveryNoteSandbox from './deliveryNote/DeliveryNoteSandbox';
import { buildDeliveryNoteHtml } from './deliveryNote/buildDeliveryNoteHtml';
import {
  DEFAULT_DELIVERY_NOTE_LAYOUT,
  resolveDeliveryNoteShowLineDiscountColumn,
} from '../../models/configuration';
import SubscriptionGuard from '../shared/billing/SubscriptionGuard';
import ProfitPlus2k8SyncStatus from '../shared/profitplus/ProfitPlus2k8SyncStatus';
import { Organization, SUPPORTED_CURRENCIES, getVesFiscalData } from '../../models/organization';
import type { ProfitPlus2k8OrgConfig } from '../../models/configuration';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useBranding } from '../../contexts/BrandingContext';
import {
  profitPlusTimestampToMs,
  PROFIT_SYNC_TIMESTAMP_SKEW_MS,
} from '../../utils/profitPlusTimestamp';
import {
  appendProfitPlusManualDeleteNoticeIfNeeded,
  getProfitPedidoFactNum,
  orderWasSyncedWithProfitPlus,
} from '../../utils/profitPlusDeleteConfirmation';
import ProfitPlusSyncHelpDrawer from '../shared/profitplus/ProfitPlusSyncHelpDrawer';
import FacturacionGuideCard from '../shared/facturacion/FacturacionGuideCard';

function capitalize(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function getApprovalStateInfo(state: string) {
  switch (state) {
    case 'pending':
      return { label: 'Pendiente', variant: 'warning' as const };
    case 'approved':
      return { label: 'Aprobado', variant: 'success' as const };
    case 'rejected':
      return { label: 'Rechazado', variant: 'danger' as const };
    default:
      return { label: 'Desconocido', variant: 'secondary' as const };
  }
}

function mapOrderDeleteError(error: any): string {
  const detail = String(error?.response?.data?.detail || error?.message || '').trim();
  const normalized = detail.toLowerCase();
  if (normalized.includes('active returns')) {
    return 'No se puede eliminar la venta porque tiene devoluciones activas. Elimina o resuelve esas devoluciones primero.';
  }
  if (normalized.includes('registered payments')) {
    return 'No se puede eliminar la venta porque su cuenta por cobrar tiene abonos registrados.';
  }
  if (normalized.includes('financial movements')) {
    return 'No se puede eliminar la venta porque su cuenta por cobrar tiene movimientos financieros.';
  }
  if (normalized.includes('cannot be restored')) {
    return 'No se puede restaurar una venta aprobada eliminada porque sus efectos de inventario y CxC ya fueron revertidos.';
  }
  return detail || 'Algo salió mal';
}

function getSaleReference(order: Order): string {
  if (order.saleNumber) {
    return `#${order.saleNumber}`;
  }
  if (order.id) {
    return `${order.id.substring(0, 8)}`;
  }
  return '#';
}

function orderHasProductIdsForProfitRefresh(order: Order): boolean {
  return Boolean(
    order.products?.some(p => String(p.productId || '').trim())
  );
}

/** Eligible to enqueue Profit → MayorX inventory pull (e.g. retry after marking invoiced by mistake). */
function canRetryProfitInventorySync(
  order: Order,
  profitPlusUiActive: boolean,
  stockManagedInMayorX: boolean
): boolean {
  return (
    Boolean(profitPlusUiActive) &&
    !stockManagedInMayorX &&
    orderWasSyncedWithProfitPlus(order) &&
    orderHasProductIdsForProfitRefresh(order)
  );
}

function getDisplayIvaAmount(order: Order): number {
  const subtotal = Number(order.subtotal) || 0;
  const total = Number(order.total) || 0;
  const stored = Number(order.ivaAmount) || 0;
  return Math.abs(subtotal + stored - total) > 0.01 ? total - subtotal : stored;
}

function getInvoiceStateInfo(invoiced?: boolean) {
  return invoiced
    ? { label: 'Facturado', variant: 'success' as const }
    : { label: 'Sin facturar', variant: 'secondary' as const };
}

function renderOrderStatusBadges(order: Order) {
  const approval = getApprovalStateInfo(order.approvalState);
  const invoice = getInvoiceStateInfo(order.invoiced);
  return (
    <div className={styles.statusStack}>
      <Badge bg={approval.variant} className={styles.statusBadge}>
        {approval.label}
      </Badge>
      <Badge
        bg={invoice.variant === 'secondary' ? 'light' : invoice.variant}
        text={invoice.variant === 'secondary' ? 'dark' : undefined}
        className={styles.statusBadge}
      >
        {invoice.label}
      </Badge>
    </div>
  );
}

function getOrdersTableColSpan(
  showAllColumns: boolean,
  showProfitPlus2k8OrderUi: boolean
): number {
  const base = showAllColumns ? 11 : 7;
  return base + (showProfitPlus2k8OrderUi ? 1 : 0);
}

/** Poll order doc after Profit export queued (approve / retry): same cadence as inventario. */
const PROFIT_SYNC_POLL_ATTEMPTS = 6;
const PROFIT_SYNC_POLL_INTERVAL_MS = 15_000;

function applyInvoiceFilterCompatibility(
  items: Order[],
  invoiceFilter: 'all' | 'invoiced' | 'pending'
): Order[] {
  if (invoiceFilter === 'invoiced') {
    return items.filter(order => order.invoiced === true);
  }
  if (invoiceFilter === 'pending') {
    // Backward compatibility: old sales may not have `invoiced` at all.
    return items.filter(order => order.invoiced !== true);
  }
  return items;
}

interface OrdersProps {
  device: string;
  addToast: (message: string) => void;
  navStatus: string;
  organizationId: string;
  organization: Organization | null;
}

export default function Orders({
  device,
  addToast,
  navStatus,
  organizationId,
  organization,
}: OrdersProps) {
  const onboarding = useOnboardingOptional();
  const creatingOrderRef = useRef(false);
  const approveGuideRefreshRef = useRef(false);
  const { showConfirmation, showWarningConfirmation } =
    useConfirmationDialog();
  const { showLoading, hideLoading } = useLoading();
  const { branding } = useBranding();
  const canWriteOrders = useCanWritePanelArea(PANEL_PERMISSIONS.ORDERS);
  const subscriptionStatus = useSubscriptionStatus(organization);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadMore, setLoadMore] = useState(false);
  const [lastDocument, setLastDocument] = useState<any>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [search, setSearch] = useState('');
  const [screen, setScreen] = useState(0);
  const [pending, setPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<Order | null>(null);
  /** When true, OrderForm opens for approval review before approving (always from list «Aprobar»). */
  const [orderFormApprovalReview, setOrderFormApprovalReview] = useState(false);
  /** Approved sale not synced to Profit: edit ERP export fields only. */
  const [orderFormProfitSyncRepair, setOrderFormProfitSyncRepair] =
    useState(false);
  const [showOrderView, setShowOrderView] = useState(false);
  const [invoiceFilter, setInvoiceFilter] = useState<
    'all' | 'invoiced' | 'pending'
  >('all');
  const [selectedOrderForView, setSelectedOrderForView] =
    useState<Order | null>(null);
  const [orderApprovalRequired, setOrderApprovalRequired] = useState(true);
  const [profitPlus2k8OrgConfig, setProfitPlus2k8OrgConfig] =
    useState<ProfitPlus2k8OrgConfig | null>(null);
  const [referenceCurrency, setReferenceCurrency] = useState('USD');
  const [currency, setCurrency] = useState('VES');
  const [deliveryNoteLayout, setDeliveryNoteLayout] = useState(
    () => DEFAULT_DELIVERY_NOTE_LAYOUT
  );
  const [showDeliveryNoteSandbox, setShowDeliveryNoteSandbox] = useState(false);
  const [retryingProfitOrderId, setRetryingProfitOrderId] = useState<string | null>(
    null
  );
  const [profitSyncPollingIds, setProfitSyncPollingIds] = useState<Set<string>>(
    () => new Set()
  );
  const [creatorDisplayByStored, setCreatorDisplayByStored] = useState<
    Record<string, OrderCreatorDisplayInfo>
  >({});
  const creatorDisplayFetchStartedRef = useRef<Set<string>>(new Set());
  const profitPollTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const pendingProfitHelpOrderIdsRef = useRef<Set<string>>(new Set());
  const [showProfitPlusHelpDrawer, setShowProfitPlusHelpDrawer] = useState(false);

  const uniqueCreatorKeys = useMemo(
    () =>
      Array.from(
        new Set(
          orders
            .map(o => (o.creatorSalesPerson || '').trim())
            .filter(Boolean)
        )
      ),
    [orders]
  );

  const renderCreatorSalesPerson = useCallback((raw: string | undefined) => {
    const s = (raw ?? '').trim();
    if (!s) return '—';
    const info = creatorDisplayByStored[s];
    if (!info) return pascalCaseWords(s);
    return (
      <span className="d-inline-flex align-items-center flex-wrap gap-1">
        <span>{info.name}</span>
        {info.isAdmin && (
          <Badge bg="secondary" className="small">
            Admin
          </Badge>
        )}
      </span>
    );
  }, [creatorDisplayByStored]);

  const groupedNumberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const getCurrencySymbol = useCallback((code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  }, []);
  const formatMoney = useCallback(
    (amount: number, code: string = referenceCurrency): string => {
      const rounded = groupedNumberFormatter.format(Number(amount) || 0);
      const symbol = getCurrencySymbol(code);
      // Non-breaking space so symbol + amount stay on one line in narrow columns
      return symbol ? `${symbol}\u00A0${rounded}` : `${rounded}`;
    },
    [referenceCurrency, getCurrencySymbol]
  );

  const profitPlus2k8Enabled = Boolean(profitPlus2k8OrgConfig?.enabled);
  const profitPlus2k8AppReady = useMemo(
    () => isProfitPlus2k8ReadyForAppFeatures(profitPlus2k8OrgConfig),
    [profitPlus2k8OrgConfig]
  );
  const profitPlus2k8StockManagedInMayorX = useMemo(
    () => isProfitPlus2k8StockManagedInMayorX(profitPlus2k8OrgConfig),
    [profitPlus2k8OrgConfig]
  );
  const showProfitPlus2k8OrderUi = profitPlus2k8Enabled && profitPlus2k8AppReady;
  const orderProfitExportActive = useMemo(
    () =>
      Boolean(profitPlus2k8OrgConfig?.enabled) &&
      isProfitPlus2k8ReadyForAppFeatures(profitPlus2k8OrgConfig) &&
      Boolean(profitPlus2k8OrgConfig?.syncAcceptedSalesToProfit) &&
      ['export_only', 'bidirectional'].includes(
        String(profitPlus2k8OrgConfig?.syncType ?? '').trim()
      ),
    [
      profitPlus2k8OrgConfig,
      profitPlus2k8OrgConfig?.enabled,
      profitPlus2k8OrgConfig?.syncAcceptedSalesToProfit,
      profitPlus2k8OrgConfig?.syncType,
      profitPlus2k8OrgConfig?.initialSetupCompleted,
    ]
  );
  const ordersListenerRef = useRef<any>(null);
  const loadOrdersRequestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // Load configuration to check if approval is required and if negative inventory is allowed
  const loadConfiguration = useCallback(async () => {
    try {
      const config = await loadConfigurationFactors(organizationId);
      setOrderApprovalRequired(config.orderApprovalRequired || false);
      setProfitPlus2k8OrgConfig(config.profitPlus2k8 ?? null);
      setReferenceCurrency(config.referenceCurrency || 'USD');
      setCurrency(config.currency || 'VES');
      setDeliveryNoteLayout(
        config.deliveryNoteLayout && Array.isArray(config.deliveryNoteLayout.blockOrder)
          ? config.deliveryNoteLayout
          : DEFAULT_DELIVERY_NOTE_LAYOUT
      );
    } catch (e) {
      console.error('Failed to load configuration:', e);
      // Default to requiring approval if config can't be loaded
      setOrderApprovalRequired(true);
      setProfitPlus2k8OrgConfig(null);
    }
  }, [organizationId]);

  const stopProfitSyncPoll = useCallback((orderId: string) => {
    const tid = profitPollTimeoutsRef.current.get(orderId);
    if (tid) {
      clearTimeout(tid);
      profitPollTimeoutsRef.current.delete(orderId);
    }
    setProfitSyncPollingIds(prev => {
      if (!prev.has(orderId)) return prev;
      const n = new Set(prev);
      n.delete(orderId);
      return n;
    });
  }, []);

  const startProfitSyncPoll = useCallback(
    (orderId: string) => {
      stopProfitSyncPoll(orderId);
      setProfitSyncPollingIds(prev => new Set(prev).add(orderId));
      const requestStartedAtMs = Date.now();
      let attempt = 0;

      const runAttempt = async () => {
        attempt += 1;
        try {
          const o = await fetchOrderById(organizationId, orderId);
          if (o) {
            setOrders(prev => {
              const idx = prev.findIndex(x => x.id === orderId);
              if (idx === -1) return prev;
              return prev.map((x, i) => (i === idx ? o : x));
            });
            const pp = o.profitPlus2k8 as { syncedAt?: unknown; exportError?: string | null } | undefined;
            const err = (pp?.exportError ?? '').trim();
            if (err) {
              pendingProfitHelpOrderIdsRef.current.delete(orderId);
              stopProfitSyncPoll(orderId);
              return;
            }
            const syncedAtMs = profitPlusTimestampToMs(pp?.syncedAt);
            if (
              syncedAtMs != null &&
              syncedAtMs >= requestStartedAtMs - PROFIT_SYNC_TIMESTAMP_SKEW_MS
            ) {
              if (pendingProfitHelpOrderIdsRef.current.delete(orderId)) {
                setShowProfitPlusHelpDrawer(true);
              }
              stopProfitSyncPoll(orderId);
              return;
            }
          }
        } catch (e) {
          console.error('Profit order sync poll', orderId, e);
        }
        if (attempt >= PROFIT_SYNC_POLL_ATTEMPTS) {
          pendingProfitHelpOrderIdsRef.current.delete(orderId);
          stopProfitSyncPoll(orderId);
          return;
        }
        const tid = setTimeout(() => {
          profitPollTimeoutsRef.current.delete(orderId);
          void runAttempt();
        }, PROFIT_SYNC_POLL_INTERVAL_MS);
        profitPollTimeoutsRef.current.set(orderId, tid);
      };

      void runAttempt();
    },
    [organizationId, stopProfitSyncPoll]
  );

  useEffect(() => {
    return () => {
      profitPollTimeoutsRef.current.forEach(t => clearTimeout(t));
      profitPollTimeoutsRef.current.clear();
    };
  }, []);

  const openProfitPlusHelpDrawer = useCallback(() => {
    setShowProfitPlusHelpDrawer(true);
  }, []);

  const closeProfitPlusHelpDrawer = useCallback(() => {
    setShowProfitPlusHelpDrawer(false);
  }, []);

  const handleRetryProfitOrderSync = useCallback(
    async (order: Order) => {
      const orderId = String(order.id || '').trim();
      if (!orderId) return;
      const issues = await collectFullProfitOrderSyncIssues(organizationId, order);

      if (issues.length > 0) {
        addToast(
          `No se puede sincronizar la venta con Profit Plus: ${issues.join('; ')}.`
        );
        return;
      }

      setRetryingProfitOrderId(orderId);
      try {
        await requestOrderSyncToProfit(organizationId, orderId);
        addToast(
          'Sincronización con Profit solicitada. El puente la procesará en breve.'
        );
        if (showProfitPlus2k8OrderUi) {
          pendingProfitHelpOrderIdsRef.current.add(orderId);
        }
        startProfitSyncPoll(orderId);
      } catch (e: any) {
        console.error(e);
        addToast(
          e?.message || 'No se pudo solicitar la sincronización con Profit.'
        );
      } finally {
        setRetryingProfitOrderId(null);
      }
    },
    [organizationId, addToast, startProfitSyncPoll, showProfitPlus2k8OrderUi]
  );

  const loadMoreOrders = useCallback(async () => {
    if (!mountedRef.current) return;

    setLoadMorePending(true);

    try {
      const invoiceQueryFilter =
        invoiceFilter === 'all'
          ? undefined
          : invoiceFilter === 'invoiced'
          ? true
          : undefined;
      const result = await loadOrders(
        organizationId,
        search,
        lastDocument,
        10,
        invoiceQueryFilter
      );
      const filteredOrders = applyInvoiceFilterCompatibility(
        result.orders,
        invoiceFilter
      );

      if (mountedRef.current) {
        setOrders(prevOrders =>
          sortOrdersByCreatedThenSaleNumber([
            ...prevOrders,
            ...filteredOrders,
          ])
        );
        setLoadMore(result.hasMore);
        setLastDocument(result.lastDoc);
        setPending(false);
        setLoadMorePending(false);
      }
    } catch (e) {
      console.log(e);
      if (mountedRef.current) {
        setLoadMorePending(false);
        setLoadMore(false);
      }
    }
  }, [search, lastDocument, organizationId, invoiceFilter]);

  const loadOrdersList = useCallback(async () => {
    if (!organizationId) return;

    const requestId = ++loadOrdersRequestIdRef.current;
    setPending(true);

    const invoiceQueryFilter =
      invoiceFilter === 'all'
        ? undefined
        : invoiceFilter === 'invoiced'
        ? true
        : undefined;

    try {
      const result = await loadOrders(
        organizationId,
        search,
        undefined,
        10,
        invoiceQueryFilter
      );
      const filteredOrders = applyInvoiceFilterCompatibility(
        result.orders,
        invoiceFilter
      );
      if (requestId !== loadOrdersRequestIdRef.current) return;
      setOrders(filteredOrders);
      setLoadMore(result.hasMore);
      setLastDocument(result.lastDoc);
    } catch (error) {
      if (requestId !== loadOrdersRequestIdRef.current) return;
      console.log(error);
      setLoadMore(false);
    } finally {
      if (requestId === loadOrdersRequestIdRef.current) {
        setPending(false);
      }
    }
  }, [organizationId, search, invoiceFilter]);

  const getOrders = useCallback(() => {
    if (isSelfHosted) {
      void loadOrdersList();
      return;
    }

    if (!mountedRef.current) return;

    setPending(true);

    if (ordersListenerRef.current !== null) {
      ordersListenerRef.current(); // Clean the listener
    }

    const invoiceQueryFilter =
      invoiceFilter === 'all'
        ? undefined
        : invoiceFilter === 'invoiced'
        ? true
        : undefined;

    ordersListenerRef.current = subscribeToOrders(
      organizationId,
      search,
      invoiceQueryFilter,
      (orders, hasMore, lastDoc) => {
        const filteredOrders = applyInvoiceFilterCompatibility(
          orders,
          invoiceFilter
        );
        if (mountedRef.current) {
          setOrders(filteredOrders);
          setLoadMore(hasMore);
          setLastDocument(lastDoc);
          setPending(false);
        }
      },
      error => {
        console.log(error);
        if (mountedRef.current) {
          setPending(false);
          setLoadMore(false);
        }
      },
      10
    );
  }, [search, organizationId, invoiceFilter, loadOrdersList]);

  // Effect for component mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    loadConfiguration();
    getOrders();

    return () => {
      mountedRef.current = false;

      if (ordersListenerRef.current !== null) {
        ordersListenerRef.current(); // Clean the listener
      }
    };
  }, [loadConfiguration, getOrders]);

  useEffect(() => {
    creatorDisplayFetchStartedRef.current.clear();
    setCreatorDisplayByStored({});
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId || uniqueCreatorKeys.length === 0) return;
    for (const stored of uniqueCreatorKeys) {
      if (creatorDisplayFetchStartedRef.current.has(stored)) continue;
      creatorDisplayFetchStartedRef.current.add(stored);
      void resolveOrderCreatorDisplay(organizationId, stored).then(info => {
        if (!mountedRef.current) return;
        setCreatorDisplayByStored(prev =>
          prev[stored] !== undefined ? prev : { ...prev, [stored]: info }
        );
      });
    }
  }, [organizationId, uniqueCreatorKeys]);

  // Helper function to check inventory availability
  const handleDeleteOrder = useCallback(
    async (order: Order) => {
      if (!mountedRef.current) return;

      // Show confirmation dialog before deleting
      const confirmCallback = async () => {
        // User confirmed - proceed with deletion
        if (!mountedRef.current) return;

        showLoading('Moviendo a Papelera');

        try {
          await deleteOrderServer(organizationId, order.id, true);
          addToast('Venta movida a papelera');
          // Note: Real-time listener will automatically update the list
          // Order will disappear from main list and appear in trash bin
        } catch (e: any) {
          console.log(e);
          addToast(mapOrderDeleteError(e));
        } finally {
          hideLoading();
        }
      };

      const approvedWarning =
        order.approvalState === 'approved'
          ? ' Esta venta ya fue aprobada: al eliminarla se restaurará inventario y se cancelará la CxC solo si no tiene abonos, ajustes, notas de crédito ni devoluciones activas. Si tiene movimientos financieros o devoluciones, la eliminación será bloqueada. Luego no podrá restaurarse; si fue un error, deberás crear una nueva venta.'
          : '';
      const baseMessage = `¿Estás seguro de que quieres mover la orden de ${
        order.clientName
      } por ${formatMoney(
        order.total
      )} a la papelera?${
        order.approvalState === 'approved' ? '' : ' Podrás restaurarla más tarde.'
      }${approvedWarning}`;
      showConfirmation(
        {
          title: 'Mover a Papelera',
          message: appendProfitPlusManualDeleteNoticeIfNeeded(
            baseMessage,
            profitPlus2k8OrgConfig,
            orderWasSyncedWithProfitPlus(order)
          ),
          confirmText: 'Mover a Papelera',
          cancelText: 'Cancelar',
        },
        confirmCallback
      );
    },
    [addToast, showConfirmation, organizationId, profitPlus2k8OrgConfig]
  );

  const handleDenyOrder = useCallback(
    async (order: Order) => {
      if (!mountedRef.current) return;

      // Show confirmation dialog before rejecting
      showConfirmation(
        {
          title: 'Rechazar Orden',
          message: `¿Estás seguro de que quieres rechazar la orden de ${
            order.clientName
          } por ${formatMoney(order.total)}?`,
          confirmText: 'Rechazar',
          cancelText: 'Cancelar',
        },
        async () => {
          // User confirmed - proceed with rejection
          if (!mountedRef.current) return;

          showLoading('Rechazando Venta');

          try {
            await rejectOrderServer(organizationId, order.id);
            addToast('Venta Rechazada!');
            // Note: Real-time listener will automatically update the list
            // Order status will change from pending to rejected
          } catch (e) {
            console.log(e);
            addToast('Algo salió mal');
          } finally {
            hideLoading();
          }
        }
      );
    },
    [addToast, showConfirmation]
  );

  const handleViewOrderModal = useCallback((order: Order) => {
    setSelectedOrderForView(order);
    setShowOrderView(true);
  }, []);

  const openCreateOrder = useCallback(() => {
    if (!canWriteOrders) return;
    creatingOrderRef.current = true;
    setOrderToEdit(null);
    setOrderFormApprovalReview(false);
    setOrderFormProfitSyncRepair(false);
    setShowForm(true);
  }, [canWriteOrders]);

  const handleEditOrderModal = useCallback((order: Order) => {
    if (!canWriteOrders) return;
    creatingOrderRef.current = false;
    setOrderToEdit(order);
    setOrderFormApprovalReview(false);
    setOrderFormProfitSyncRepair(false);
    setShowForm(true);
  }, [canWriteOrders]);

  const handleProfitSyncRepairModal = useCallback((order: Order) => {
    if (!canWriteOrders) return;
    setOrderToEdit(order);
    setOrderFormApprovalReview(false);
    setOrderFormProfitSyncRepair(true);
    setShowForm(true);
  }, [canWriteOrders]);

  const handleCloseOrderForm = useCallback(() => {
    setShowForm(false);
    setOrderToEdit(null);
    setOrderFormApprovalReview(false);
    setOrderFormProfitSyncRepair(false);
  }, []);

  const canProfitSyncRepairOrder = useCallback(
    (o: Order) =>
      Boolean(
        orderProfitExportActive &&
          showProfitPlus2k8OrderUi &&
          o.approvalState === 'approved' &&
          !orderWasSyncedWithProfitPlus(o)
      ),
    [orderProfitExportActive, showProfitPlus2k8OrderUi]
  );

  const handleProfitSyncRepairAfterSave = useCallback(
    async (orderId: string) => {
      const id = String(orderId || '').trim();
      if (!id) return;
      try {
        const fresh = await getOrderServer(organizationId, id);
        const issues = await collectFullProfitOrderSyncIssues(
          organizationId,
          fresh
        );
        if (issues.length > 0) {
          addToast(
            `Datos Profit guardados. Aún no se puede sincronizar automáticamente: ${issues.join(
              '; '
            )}.`
          );
          return;
        }
        await requestOrderSyncToProfit(organizationId, id);
        addToast(
          'Sincronización con Profit solicitada. El puente la procesará en breve.'
        );
        if (showProfitPlus2k8OrderUi) {
          pendingProfitHelpOrderIdsRef.current.add(id);
        }
        startProfitSyncPoll(id);
      } catch (e: any) {
        console.error(e);
        addToast(
          e?.message ||
            'No se pudo solicitar la sincronización tras guardar los datos Profit.'
        );
      }
    },
    [
      organizationId,
      addToast,
      startProfitSyncPoll,
      showProfitPlus2k8OrderUi,
    ]
  );

  const handleProfitAwareAccept = useCallback((order: Order) => {
    creatingOrderRef.current = false;
    setOrderToEdit(order);
    setOrderFormApprovalReview(true);
    setOrderFormProfitSyncRepair(false);
    setShowForm(true);
  }, []);

  const findPendingApprovalOrder = useCallback((list: Order[]) => {
    return (
      list.find(o => o.approvalState === 'pending') ||
      list.find(
        o => o.approvalState !== 'approved' && o.approvalState !== 'rejected'
      ) ||
      null
    );
  }, []);

  // Keep guide counts in sync with the list the user actually sees
  useEffect(() => {
    if (!onboarding?.showTour) return;
    const approved = orders.filter(o => o.approvalState === 'approved').length;
    onboarding.reportLiveCounts({
      orders: orders.length,
      approvedOrders: approved,
    });
  }, [onboarding?.showTour, onboarding?.reportLiveCounts, orders]);

  // Rail "Crear venta" while already on /orders — open the form (never a no-op)
  useEffect(() => {
    if (!onboarding?.showTour) return;
    if (onboarding.pendingAction !== 'open-create-order') return;
    if (showForm) {
      onboarding.clearPendingAction();
      return;
    }
    openCreateOrder();
    onboarding.clearPendingAction();
  }, [
    onboarding?.showTour,
    onboarding?.pendingAction,
    onboarding?.clearPendingAction,
    showForm,
    openCreateOrder,
  ]);

  // After creating a sale (or rail CTA), open the pending approval flow automatically
  useEffect(() => {
    if (!onboarding?.showTour) {
      approveGuideRefreshRef.current = false;
      return;
    }
    if (onboarding.pendingAction !== 'open-approve') {
      approveGuideRefreshRef.current = false;
      return;
    }
    // Ensure pending sales are visible in the list
    if (invoiceFilter !== 'all') {
      setInvoiceFilter('all');
      return;
    }
    if (pending || showForm) return;
    const pendingOrder = findPendingApprovalOrder(orders);
    if (!pendingOrder) {
      // Already-approved orgs (or race): don't spin forever on "Crear venta"
      const hasApproved = orders.some(o => o.approvalState === 'approved');
      if (hasApproved && onboarding.currentStepId === 'approve') {
        approveGuideRefreshRef.current = false;
        onboarding.clearPendingAction();
        void onboarding.advanceAfterMutation('approve');
        return;
      }
      if (!approveGuideRefreshRef.current) {
        approveGuideRefreshRef.current = true;
        getOrders();
      }
      return;
    }
    approveGuideRefreshRef.current = false;
    handleProfitAwareAccept(pendingOrder);
    onboarding.clearPendingAction();
  }, [
    onboarding,
    onboarding?.pendingAction,
    onboarding?.showTour,
    onboarding?.currentStepId,
    orders,
    pending,
    showForm,
    invoiceFilter,
    findPendingApprovalOrder,
    handleProfitAwareAccept,
    getOrders,
  ]);

  const handleCloseOrderView = useCallback(() => {
    setShowOrderView(false);
  }, []);

  const handleOrderViewExited = useCallback(() => {
    setSelectedOrderForView(null);
  }, []);

  const resolveDeliveryNoteRecipient = useCallback(
    async (order: Order): Promise<string> => {
      if (!order.clientId) return '';
      try {
        const clientRef = doc(
          firestore,
          'organizations',
          organizationId,
          'clients',
          order.clientId
        );
        const clientSnap = await getDoc(clientRef);
        if (!clientSnap.exists()) return '';
        const clientData = clientSnap.data();
        return String(clientData?.email || '').trim();
      } catch (error) {
        console.warn('Failed to resolve delivery-note recipient email', error);
        return '';
      }
    },
    [organizationId]
  );

  const handleSendDeliveryNote = useCallback(
    async (
      order: Order,
      recipientEmail: string,
      options: DeliveryNoteSendOptions
    ) => {
      if (!mountedRef.current) return;
      showLoading('Enviando Nota de Entrega');
      try {
        const layout = deliveryNoteLayout ?? DEFAULT_DELIVERY_NOTE_LAYOUT;
        const logoUrl = (branding?.logo || organization?.logoUrl || '').trim();
        const deliveryNoteHtml = buildDeliveryNoteHtml(
          order,
          {
            showAmounts: options.showAmounts,
            amountCurrencyMode: options.amountCurrencyMode,
            showLineDiscountColumn: options.showLineDiscountColumn,
          },
          layout,
          {
            logoUrl,
            logoAlt: organization?.name || 'Logo',
            referenceCurrency: referenceCurrency || 'USD',
            organizationCurrency: currency || 'VES',
            formatMoney,
            formatNumber: formatExchangeRate,
            fiscalData: getVesFiscalData(organization) ?? null,
          },
          true
        );
        await sendDeliveryNoteServer(organizationId, order.id, recipientEmail, {
          showAmounts: options.showAmounts,
          amountCurrencyMode: options.amountCurrencyMode,
          showLineDiscountColumn: options.showLineDiscountColumn,
          deliveryNoteHtml,
        });
        addToast('Nota de Entrega enviada');
      } catch (e: any) {
        console.log(e);
        addToast(e?.message || 'No se pudo enviar la Nota de Entrega');
      } finally {
        hideLoading();
      }
    },
    [
      addToast,
      organizationId,
      deliveryNoteLayout,
      branding?.logo,
      organization?.logoUrl,
      organization?.name,
      organization,
      referenceCurrency,
      currency,
      formatMoney,
      formatExchangeRate,
    ]
  );

  const queueProfitInventoryRefreshForOrder = useCallback(
    async (order: Order): Promise<boolean> => {
      if (
        !showProfitPlus2k8OrderUi ||
        profitPlus2k8StockManagedInMayorX ||
        !orderWasSyncedWithProfitPlus(order) ||
        !order.products?.length
      ) {
        return false;
      }
      const productIds = Array.from(
        new Set(
          order.products
            .map(p => String(p.productId || '').trim())
            .filter(Boolean)
        )
      );
      if (!productIds.length) {
        return false;
      }
      try {
        await Promise.allSettled(
          productIds.map(pid =>
            requestProductInventoryRefreshFromProfit(organizationId, pid)
          )
        );
        return true;
      } catch (queueErr) {
        console.warn('Could not queue Profit Plus inventory refresh', queueErr);
        return false;
      }
    },
    [organizationId, showProfitPlus2k8OrderUi, profitPlus2k8StockManagedInMayorX]
  );

  const handleMarkAsInvoiced = useCallback(
    async (order: Order) => {
      if (!mountedRef.current) return;

      if (order.invoiced) {
        if (
          !canRetryProfitInventorySync(
            order,
            showProfitPlus2k8OrderUi,
            profitPlus2k8StockManagedInMayorX
          )
        ) {
          addToast('Esta venta ya está facturada.');
          return;
        }
        showWarningConfirmation(
          'Actualizar inventario desde Profit Plus',
          'Esta venta ya está marcada como facturada. ¿Desea volver a encolar la actualización de inventario desde Profit Plus para los productos de la venta?',
          async () => {
            if (!mountedRef.current) return;
            showLoading('Encolando actualización de inventario...');
            try {
              const inventoryRefreshQueued =
                await queueProfitInventoryRefreshForOrder(order);
              addToast(
                inventoryRefreshQueued
                  ? 'Actualización de inventario desde Profit Plus encolada.'
                  : 'No se pudo encolar la actualización de inventario.'
              );
            } catch (e) {
              console.warn(e);
              addToast('No se pudo encolar la actualización de inventario.');
            } finally {
              hideLoading();
            }
          },
          {
            confirmText: 'Sí, encolar',
            cancelText: 'Cancelar',
          }
        );
        return;
      }

      showLoading('Marcando como facturado');
      try {
        await markOrderAsInvoicedServer(organizationId, order.id);
        const inventoryRefreshQueued =
          await queueProfitInventoryRefreshForOrder(order);
        if (mountedRef.current) {
          setSelectedOrderForView(prev =>
            prev && prev.id === order.id ? { ...prev, invoiced: true } : prev
          );
        }
        addToast(
          inventoryRefreshQueued
            ? 'Venta marcada como facturada. Actualización de inventario desde Profit Plus encolada.'
            : 'Venta marcada como facturada'
        );
      } catch (e: any) {
        console.log(e);
        addToast(e?.message || 'No se pudo marcar como facturada');
      } finally {
        hideLoading();
      }
    },
    [
      addToast,
      organizationId,
      showProfitPlus2k8OrderUi,
      profitPlus2k8StockManagedInMayorX,
      queueProfitInventoryRefreshForOrder,
      showWarningConfirmation,
    ]
  );

  const handlePrintDeliveryNote = useCallback(
    (order: Order, options: DeliveryNotePrintOptions) => {
      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        addToast('No se pudo abrir la ventana de impresión');
        return;
      }
      const layout = deliveryNoteLayout ?? DEFAULT_DELIVERY_NOTE_LAYOUT;
      const logoUrl = (branding?.logo || organization?.logoUrl || '').trim();
      const html = buildDeliveryNoteHtml(
        order,
        {
          showAmounts: options.showAmounts,
          amountCurrencyMode: options.amountCurrencyMode,
          showLineDiscountColumn: options.showLineDiscountColumn,
        },
        layout,
        {
          logoUrl,
          logoAlt: organization?.name || 'Logo',
          referenceCurrency: referenceCurrency || 'USD',
          organizationCurrency: currency || 'VES',
          formatMoney,
          formatNumber: formatExchangeRate,
          fiscalData: getVesFiscalData(organization) ?? null,
        }
      );
      printWindow.document.write(html);
      printWindow.document.close();

      const waitForImages = (doc: Document): Promise<void> => {
        const imgs = doc.querySelectorAll('img');
        if (imgs.length === 0) return Promise.resolve();
        const timeout = 8000;
        const loadPromises = Array.from(imgs).map(
          (img) =>
            new Promise<void>((resolve) => {
              if (img.complete) {
                resolve();
                return;
              }
              const onDone = () => {
                img.removeEventListener('load', onDone);
                img.removeEventListener('error', onDone);
                resolve();
              };
              img.addEventListener('load', onDone);
              img.addEventListener('error', onDone);
            })
        );
        return Promise.race([
          Promise.all(loadPromises).then(() => undefined),
          new Promise<void>((resolve) => setTimeout(resolve, timeout)),
        ]);
      };

      waitForImages(printWindow.document).then(() => {
        printWindow.focus();
        printWindow.print();
      });
    },
    [
      addToast,
      branding,
      currency,
      deliveryNoteLayout,
      organization?.logoUrl,
      organization?.name,
      referenceCurrency,
      formatMoney,
      formatExchangeRate,
    ]
  );

  return (
    <Container fluid className="fade-in p-0 p-md-4">
      {isLoading ? <Loading /> : null}

      <SubscriptionGuard organization={organization}>
        {/* Header Section */}
        <div className="white-card mb-4">
          <div className="text-center text-md-start">
            <h2 className="fw-bold mb-3">Ventas</h2>
            {!orderApprovalRequired && (
              <Badge bg="info" className="mb-3">
                Aprobación automática activada
              </Badge>
            )}
          </div>
        </div>

        {screen === 0 ? (
          <>
            {/* Search and Actions Section */}
            <div className="white-card mb-4">
              <div className="d-flex flex-column flex-xl-row gap-3 align-items-stretch align-items-xl-center">
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FiSearch />
                    </InputGroup.Text>
                    <FormControl
                      type="text"
                      value={search}
                      placeholder="Buscar ventas por cliente o número..."
                      onChange={e => {
                        if (mountedRef.current) {
                          setSearch(e.currentTarget.value);
                        }
                      }}
                      onKeyPress={(
                        e: React.KeyboardEvent<HTMLInputElement>
                      ) => {
                        if (e.key === 'Enter') {
                          getOrders();
                        }
                      }}
                    />
                    <Button variant="outline-secondary" onClick={getOrders}>
                      Buscar
                    </Button>
                  </InputGroup>
                </div>
                <div className="d-flex flex-wrap gap-2 flex-shrink-0 align-items-center">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setScreen(2)}
                    title="Ver órdenes eliminadas"
                    className="text-nowrap"
                  >
                    <FiTrash2 className="me-1" />
                    Papelera
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowDeliveryNoteSandbox(true)}
                    title="Personalizar diseño de la Nota de Entrega"
                    className="text-nowrap"
                  >
                    <FiPalette className="me-1" />
                    <span className="d-none d-md-inline">Nota de Entrega</span>
                    <span className="d-md-none">Nota</span>
                  </Button>
                  {orderProfitExportActive && (
                    <Button
                      variant="outline-info"
                      onClick={openProfitPlusHelpDrawer}
                      className="text-nowrap"
                      title="Guía de pasos en Profit Plus 2k8 después de sincronizar una venta"
                    >
                      <FiQuestionCircle className="me-1" />
                      <span className="d-none d-lg-inline">
                        Ayuda Profit Plus
                      </span>
                      <span className="d-lg-none">Ayuda</span>
                    </Button>
                  )}
                  <RequirePanelWrite area={PANEL_PERMISSIONS.ORDERS}>
                    <Button
                      variant="primary"
                      onClick={openCreateOrder}
                      className="text-nowrap"
                    >
                      <FiPlus className="me-1" />
                      Crear Venta
                    </Button>
                  </RequirePanelWrite>
                </div>
              </div>
            </div>

            <PanelReadOnlyBanner area={PANEL_PERMISSIONS.ORDERS} />

            <FacturacionGuideCard
              variant="orders"
              profitPlusAppReady={profitPlus2k8AppReady}
              profitExportPedidosActive={orderProfitExportActive}
              onOpenProfitInvoiceHelp={
                orderProfitExportActive ? openProfitPlusHelpDrawer : undefined
              }
              visualTone="soft"
              initiallyExpanded={false}
              className="mb-3"
            />

            <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
              <Form.Group className="d-flex align-items-center mb-0">
                <Form.Label className="mb-0 me-2">Facturación:</Form.Label>
                <Form.Select
                  size="sm"
                  style={{ width: 200 }}
                  value={invoiceFilter}
                  onChange={e =>
                    setInvoiceFilter(
                      e.currentTarget.value as 'all' | 'invoiced' | 'pending'
                    )
                  }
                >
                  <option value="all">Todas</option>
                  <option value="invoiced">Facturadas</option>
                  <option value="pending">Pendientes</option>
                </Form.Select>
              </Form.Group>
              <Form.Check
                type="switch"
                id="toggle-all-order-columns"
                label="Ver todas las columnas"
                checked={showAllColumns}
                onChange={e => setShowAllColumns(e.currentTarget.checked)}
              />
            </div>

            {onboarding?.showTour &&
              !pending &&
              orders.length === 0 &&
              onboarding.currentStepId === 'order' && (
                <OnboardingEmptyHero
                  stepId="order"
                  onPrimary={openCreateOrder}
                />
              )}

            {onboarding?.showTour &&
              !pending &&
              onboarding.currentStepId === 'approve' &&
              !orders.some(o => o.approvalState === 'approved') && (
                <OnboardingEmptyHero
                  stepId="approve"
                  onPrimary={() => {
                    const pendingOrder = findPendingApprovalOrder(orders);
                    if (pendingOrder) {
                      handleProfitAwareAccept(pendingOrder);
                    } else {
                      onboarding.requestOpenApprove();
                      getOrders();
                      addToast(
                        'Buscando la venta pendiente para aprobar…'
                      );
                    }
                  }}
                  primaryLabel="Aprobar venta pendiente"
                />
              )}

            {pending === true ? (
              <div className="d-flex justify-content-start mt-2">
                <Spinner animation="border" />
              </div>
            ) : orders.length > 0 ? (
              <>
                {/* Mobile Card View */}
                <div className="d-block d-lg-none">
                  {orders.map((o, i) => (
                    <Card
                      key={o.id || i}
                      className={`mb-3 border-0 shadow-sm ${styles.orderCard} ${listStyles.clickableCard}`}
                      onClick={() => handleViewOrderModal(o)}
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                          <div className="min-w-0">
                            <div className={styles.referenceMain}>
                              {getSaleReference(o)}
                            </div>
                            <h6 className={`mb-1 mt-1 ${styles.primaryLine}`}>
                              {o.clientName}
                            </h6>
                            <div className={styles.secondaryLine}>
                              {renderCreatorSalesPerson(o.creatorSalesPerson)}
                            </div>
                            {getProfitPedidoFactNum(o) != null && (
                              <div className={styles.secondaryLine}>
                                Profit pedido {getProfitPedidoFactNum(o)}
                              </div>
                            )}
                          </div>
                          <div className={styles.statusStack}>
                            <Badge
                              bg={getApprovalStateInfo(o.approvalState).variant}
                              className={styles.statusBadge}
                            >
                              {getApprovalStateInfo(o.approvalState).label}
                            </Badge>
                            <Badge
                              bg={
                                getInvoiceStateInfo(o.invoiced).variant ===
                                'secondary'
                                  ? 'light'
                                  : getInvoiceStateInfo(o.invoiced).variant
                              }
                              text={
                                getInvoiceStateInfo(o.invoiced).variant ===
                                'secondary'
                                  ? 'dark'
                                  : undefined
                              }
                              className={styles.statusBadge}
                            >
                              {getInvoiceStateInfo(o.invoiced).label}
                            </Badge>
                          </div>
                        </div>

                        <div className={`mb-3 ${styles.mobileMetaGrid}`}>
                          <div className={styles.mobileMetaItem}>
                            <span className={styles.mobileMetaLabel}>
                              Registro
                            </span>
                            <span className={styles.mobileMetaValue}>
                              {formatDateEsFromFirestore(
                                o.recordedAt ?? o.created
                              )}
                              <span className={`d-block ${styles.dateTimeMeta}`}>
                                {formatTimeEsFromFirestore(
                                  o.recordedAt ?? o.created
                                )}
                              </span>
                            </span>
                          </div>
                          <div className={styles.mobileMetaItem}>
                            <span className={styles.mobileMetaLabel}>Pago</span>
                            <span className={styles.mobileMetaValue}>
                              {o.paymentMethod}
                              {o.paymentCurrency
                                ? ` · ${o.paymentCurrency}`
                                : ''}
                            </span>
                          </div>
                          <div className={styles.mobileMetaItem}>
                            <span className={styles.mobileMetaLabel}>
                              Total
                            </span>
                            <span className={styles.mobileTotal}>
                              {formatMoney(o.total)}
                            </span>
                          </div>
                          {showProfitPlus2k8OrderUi &&
                            orderProfitExportActive &&
                            o.approvalState === 'approved' && (
                              <div
                                className={styles.mobileMetaItem}
                                style={{ gridColumn: '1 / -1' }}
                              >
                                <span className={styles.mobileMetaLabel}>
                                  Profit Plus 2k8
                                </span>
                                <span className={styles.mobileMetaValue}>
                                  <ProfitPlus2k8SyncStatus
                                    syncedAt={o.profitPlus2k8?.syncedAt}
                                    exportError={o.profitPlus2k8?.exportError}
                                    eligibleForProfitExport={
                                      orderProfitExportActive
                                    }
                                    onRetryProfitExport={
                                      orderProfitExportActive
                                        ? () => handleRetryProfitOrderSync(o)
                                        : undefined
                                    }
                                    retryPending={
                                      retryingProfitOrderId === o.id
                                    }
                                    polling={profitSyncPollingIds.has(o.id)}
                                    ineligibleErrorHint="Revisa la configuración de exportación de ventas y los datos del pedido."
                                  />
                                </span>
                              </div>
                            )}
                        </div>

                        <OrderRowActions
                          order={o}
                          orderApprovalRequired={orderApprovalRequired}
                          layout="mobile"
                          canWrite={canWriteOrders}
                          canProfitSyncRepair={canProfitSyncRepairOrder(o)}
                          canRetryProfitInventorySync={canRetryProfitInventorySync(
                            o,
                            showProfitPlus2k8OrderUi,
                            profitPlus2k8StockManagedInMayorX
                          )}
                          onView={handleViewOrderModal}
                          onApprove={handleProfitAwareAccept}
                          onReject={handleDenyOrder}
                          onMarkAsInvoiced={handleMarkAsInvoiced}
                          onEdit={handleEditOrderModal}
                          onProfitSyncRepair={handleProfitSyncRepairModal}
                          onDelete={handleDeleteOrder}
                        />
                      </Card.Body>
                    </Card>
                  ))}

                  {/* Mobile Load More */}
                  {loadMore === true && (
                    <div className="text-center mt-3">
                      {loadMorePending ? (
                        <Spinner animation="border" />
                      ) : (
                        <Button variant="primary" onClick={loadMoreOrders}>
                          Cargar Más
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Desktop Table View */}
                <Card className={`d-none d-lg-block border-0 shadow-sm ${listStyles.tableCard} ${styles.ordersTableCard}`}>
                  <Card.Body className="p-0">
                <div
                  className={`table-responsive ${listStyles.tableWrapper} ${styles.ordersTableScroll}`}
                >
                  <Table
                    responsive
                    hover
                    borderless
                    className={`${listStyles.dataTable} ${
                      showAllColumns ? listStyles.noWrap : ''
                    } mb-0`}
                  >
                    <thead>
                      <tr>
                        <th className={listStyles.referenceCol}>Venta</th>
                        <th>Cliente</th>
                        <th>Vendedor</th>
                        {showAllColumns && (
                          <>
                            <th>Forma de pago</th>
                            <th>Moneda</th>
                          </>
                        )}
                        <th>Registro</th>
                        <th>Estado</th>
                        {showAllColumns && (
                          <>
                            <th className={listStyles.moneyHeader}>Subtotal</th>
                            <th className={listStyles.moneyHeader}>Impuestos</th>
                          </>
                        )}
                        <th className={listStyles.moneyHeader}>Total</th>
                        {showProfitPlus2k8OrderUi && (
                          <th className={styles.profitCol}>Profit Plus 2k8</th>
                        )}
                        <th
                          className={`${listStyles.stickyRightHeader} ${listStyles.actionsCol}`}
                        >
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o, i) => (
                        <tr
                          key={o.id || i}
                          className={listStyles.clickableRow}
                          onClick={() => handleViewOrderModal(o)}
                        >
                          <td className={`${listStyles.referenceCol} ${styles.referenceCell}`}>
                            <div className={styles.referenceMain}>
                              {getSaleReference(o)}
                            </div>
                            {getProfitPedidoFactNum(o) != null && (
                              <div className={styles.secondaryLine}>
                                Profit {getProfitPedidoFactNum(o)}
                              </div>
                            )}
                          </td>
                          <td className={styles.clientCell}>
                            <div className={styles.primaryLine}>{o.clientName}</div>
                          </td>
                          <td>{renderCreatorSalesPerson(o.creatorSalesPerson)}</td>
                          {showAllColumns && (
                            <>
                              <td className="text-muted">{o.paymentMethod}</td>
                              <td>{o.paymentCurrency || '—'}</td>
                            </>
                          )}
                          <td className={styles.dateCell}>
                            <div>
                              {formatDateEsFromFirestore(
                                o.recordedAt ?? o.created
                              )}
                            </div>
                            <div className={styles.dateTimeMeta}>
                              {formatTimeEsFromFirestore(
                                o.recordedAt ?? o.created
                              )}
                            </div>
                          </td>
                          <td className={styles.statusCell}>
                            {renderOrderStatusBadges(o)}
                          </td>
                          {showAllColumns && (
                            <>
                              <td className={listStyles.moneyCell}>
                                {formatMoney(o.subtotal)}
                              </td>
                              <td className={listStyles.moneyCell}>
                                {formatMoney(getDisplayIvaAmount(o))}
                              </td>
                            </>
                          )}
                          <td className={`${listStyles.moneyCell} ${styles.totalCell}`}>
                            {formatMoney(o.total)}
                          </td>
                          {showProfitPlus2k8OrderUi && (
                            <td className={styles.profitCell}>
                              {!orderProfitExportActive ? (
                                <span className="text-muted small">—</span>
                              ) : o.approvalState !== 'approved' ? (
                                <span className="text-muted small">—</span>
                              ) : (
                                <ProfitPlus2k8SyncStatus
                                  syncedAt={o.profitPlus2k8?.syncedAt}
                                  exportError={o.profitPlus2k8?.exportError}
                                  eligibleForProfitExport={orderProfitExportActive}
                                  onRetryProfitExport={
                                    orderProfitExportActive
                                      ? () => handleRetryProfitOrderSync(o)
                                      : undefined
                                  }
                                  retryPending={retryingProfitOrderId === o.id}
                                  polling={profitSyncPollingIds.has(o.id)}
                                  ineligibleErrorHint="Revisa la configuración de exportación de ventas y los datos del pedido."
                                />
                              )}
                            </td>
                          )}
                          <td
                            className={`${listStyles.stickyRightCell} ${listStyles.actionsCol} ${styles.actionsColNarrow} ${styles.actionsStickyCell}`}
                            onClick={e => e.stopPropagation()}
                          >
                            <OrderRowActions
                              order={o}
                              orderApprovalRequired={orderApprovalRequired}
                              layout="table"
                              canWrite={canWriteOrders}
                              canProfitSyncRepair={canProfitSyncRepairOrder(o)}
                              canRetryProfitInventorySync={canRetryProfitInventorySync(
                                o,
                                showProfitPlus2k8OrderUi,
                                profitPlus2k8StockManagedInMayorX
                              )}
                              onView={handleViewOrderModal}
                              onApprove={handleProfitAwareAccept}
                              onReject={handleDenyOrder}
                              onMarkAsInvoiced={handleMarkAsInvoiced}
                              onEdit={handleEditOrderModal}
                              onProfitSyncRepair={handleProfitSyncRepairModal}
                              onDelete={handleDeleteOrder}
                            />
                          </td>
                        </tr>
                      ))}
                      {loadMore === true ? (
                        <tr>
                          <td
                            colSpan={getOrdersTableColSpan(
                              showAllColumns,
                              showProfitPlus2k8OrderUi
                            )}
                            className="text-center"
                          >
                            {loadMorePending ? (
                              <Spinner animation="border" />
                            ) : (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={loadMoreOrders}
                              >
                                Cargar Más
                              </Button>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                </div>
                  </Card.Body>
                </Card>
              </>
            ) : onboarding?.showTour &&
              (onboarding.currentStepId === 'order' ||
                onboarding.currentStepId === 'approve') ? null : (
              <p>No hay ventas para el filtro seleccionado</p>
            )}
          </>
        ) : null}

        {screen === 2 ? (
          <TrashBin
            device={device}
            addToast={addToast}
            navStatus={navStatus}
            organizationId={organizationId}
            onBack={() => setScreen(0)}
          />
        ) : null}

        <OrderForm
          show={showForm}
          order={orderToEdit ?? undefined}
          approvalReviewMode={orderFormApprovalReview}
          profitSyncRepairMode={orderFormProfitSyncRepair}
          onClose={handleCloseOrderForm}
          onSaved={() => {
            const wasCreate = creatingOrderRef.current;
            const wasApprovalReview = orderFormApprovalReview;
            creatingOrderRef.current = false;
            loadConfiguration();
            getOrders();
            if (
              wasCreate &&
              !wasApprovalReview &&
              onboarding?.showTour
            ) {
              addToast('Venta creada. Ahora debes aprobarla.');
              void onboarding.advanceAfterMutation('order');
            }
          }}
          onApprovalReviewSuccess={orderId => {
            if (orderProfitExportActive) {
              if (showProfitPlus2k8OrderUi) {
                pendingProfitHelpOrderIdsRef.current.add(orderId);
              }
              startProfitSyncPoll(orderId);
            }
            if (onboarding?.showTour) {
              void onboarding.advanceAfterMutation('approve');
            }
          }}
          onProfitSyncRepairSaved={handleProfitSyncRepairAfterSave}
          organizationId={organizationId}
          addToast={addToast}
        />

        {selectedOrderForView && (
          <OrderView
            show={showOrderView}
            onClose={handleCloseOrderView}
            onExited={handleOrderViewExited}
            order={selectedOrderForView}
            referenceCurrency={referenceCurrency}
            currency={currency}
            defaultDeliveryNoteShowAmounts={deliveryNoteLayout?.defaultShowAmounts}
            defaultDeliveryNoteAmountCurrencyMode={deliveryNoteLayout?.defaultAmountCurrencyMode}
            defaultDeliveryNoteShowLineDiscountColumn={resolveDeliveryNoteShowLineDiscountColumn(
              deliveryNoteLayout
            )}
            onPrepareDeliveryNoteRecipient={resolveDeliveryNoteRecipient}
            onSendDeliveryNote={handleSendDeliveryNote}
            onPrintDeliveryNote={handlePrintDeliveryNote}
            onMarkAsInvoiced={handleMarkAsInvoiced}
            isProfitInventoryRetryAvailable={o =>
              canRetryProfitInventorySync(
                o,
                showProfitPlus2k8OrderUi,
                profitPlus2k8StockManagedInMayorX
              )
            }
          />
        )}

        <DeliveryNoteSandbox
          show={showDeliveryNoteSandbox}
          onClose={() => setShowDeliveryNoteSandbox(false)}
          organizationId={organizationId}
          organizationName={organization?.name || ''}
          logoUrl={organization?.logoUrl || ''}
          branding={branding}
          referenceCurrency={referenceCurrency}
          organizationCurrency={currency}
          formatMoney={formatMoney}
          addToast={addToast}
          onSaved={loadConfiguration}
          fiscalData={getVesFiscalData(organization) ?? undefined}
        />

        <ProfitPlusSyncHelpDrawer
          show={showProfitPlusHelpDrawer}
          onHide={closeProfitPlusHelpDrawer}
          title="Profit Plus 2k8: facturar una venta sincronizada"
          intro="Despues de sincronizar la venta con Profit Plus 2k8:"
          bullets={[
            'Ve al programa Profit Plus 2k8.',
            'Abre la ventana de procesos.',
            'Selecciona la opción de pedidos.',
            'Busca insertando el número de pedido en el campo de búsqueda.',
            'Genera la factura'
          ]}
          gif="orderInvoiceFromSale"
          gifAlt="Guia visual para generar la factura desde el pedido en Profit Plus 2k8"
        />
      </SubscriptionGuard>
    </Container>
  );
}
